import type { SagittaResult, UnitSystem } from './types';
import { PRECISION_LEVELS, UNIT_SUFFIX } from './types';

function roundToDecimals(n: number, decimals: number): string {
	const f = 10 ** decimals;
	return (Math.round((n + Number.EPSILON) * f) / f).toFixed(decimals);
}

export function formatDrawingTolerance(
	result: SagittaResult,
	unit: UnitSystem
): string {
	const suf = UNIT_SUFFIX[unit];
	const ip = result.inputTolPlus;
	const im = result.inputTolMinus;
	if (ip == null && im == null) return '';
	if (result.isSymmetric && ip != null) {
		return `+/- ${roundToDecimals(ip, unit === 'inches' ? 4 : 3)} ${suf}`;
	}
	if (ip != null && im != null) {
		return `+${roundToDecimals(ip, unit === 'inches' ? 4 : 3)} / −${roundToDecimals(im, unit === 'inches' ? 4 : 3)} ${suf}`;
	}
	return `+${ip ?? 0} ${suf}`;
}

export function buildClipboardSummary(
	unit: UnitSystem,
	result: SagittaResult,
	chord: number,
	radiusDisplay: number,
	inputMode: 'radius' | 'diameter'
): string {
	const suf = UNIT_SUFFIX[unit];
	const hasTol = result.hasToleranceDisplay;
	const lines: string[] = [
		'Arc Sagitta Calculation',
		'-----------------------',
		`Unit:             ${unit === 'inches' ? 'Inches' : 'Millimeters'}`,
		`Chord Length:     ${chord} ${suf}`,
		`${inputMode === 'radius' ? 'Radius' : 'Diameter'}:           ${radiusDisplay} ${suf}`,
		'',
		'Full Precision:',
		`  Nominal Sagitta (s):        ${result.nominal} ${suf}`,
	];
	if (hasTol) {
		lines.push(
			`  Upper Limit:                ${result.upperLimit ?? '—'} ${suf}`,
			`  Lower Limit:                ${result.lowerLimit ?? '—'} ${suf}`,
			`  Radius Adjusted Tolerance:  +${result.tolPlus ?? 0} / -${result.tolMinus ?? 0} ${suf}`,
			`  Drawing Tolerance (input):  ${formatDrawingTolerance(result, unit)}`
		);
	}
	const levels = PRECISION_LEVELS[unit];
	lines.push('');
	for (const l of levels) {
		const d = l.decimals;
		const nom = roundToDecimals(result.nominal, d);
		if (hasTol) {
			const atp = roundToDecimals(result.tolPlus ?? 0, d);
			const atm = roundToDecimals(result.tolMinus ?? 0, d);
			lines.push(
				`${l.label}:`,
				`  Nominal: ${nom} ${suf} | Adjusted Tol: +${atp} / -${atm} ${suf} | Drawing Tol: ${formatDrawingTolerance(result, unit)}`
			);
		} else {
			lines.push(`${l.label}:`, `  Nominal: ${nom} ${suf}`);
		}
	}
	return lines.join('\n');
}
